import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import messages from "../../../assets/locale/messages";
import { CupIcon } from "../../../utils/Icons/CupIcon";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import "./PendingApproval.scss";

/**
 * Title uses {status} in locale strings; only that word is wrapped with the accent style.
 */
const renderPendingTitle = (t, status) => {
  const template = t.title;
  if (!template.includes("{status}")) {
    return template;
  }
  const [beforePlaceholder, afterPlaceholder] = template.split("{status}");
  const beforeLines = beforePlaceholder.split("\n");
  const line1 = beforeLines[0];
  const line2Prefix = beforeLines.slice(1).join("\n");

  return (
    <>
      <span className="pending-approval-step__title-line">{line1.trim()}</span>
      <span className="pending-approval-step__title-line2">
        {line2Prefix ? (
          <span className="pending-approval-step__title-suffix">
            {line2Prefix.trim()}{" "}
          </span>
        ) : null}
        <span className="pending-approval-step__title-highlight">{status}</span>
        <span className="pending-approval-step__title-suffix">
          {afterPlaceholder}
        </span>
      </span>
    </>
  );
};

const renderPendingDescription = (t) => {
  const template = t.description ?? "";
  const name = t.platformName ?? "";
  if (!template.includes("{platformName}")) return template;
  return template.split("{platformName}").flatMap((part, i, parts) =>
    i < parts.length - 1
      ? [
          part,
          <strong
            key={i}
            className="pending-approval-step__description-emphasis"
          >
            {name}
          </strong>,
        ]
      : [part]
  );
};

/**
 * Shown inside AuthLayout after OTP when the account awaits admin approval.
 * Renders nothing for other statuses so the parent can swap views later.
 */
const PendingApproval = ({ status, email }) => {
  const lang = useSelector((state) => state.locale.lang);
  const t = messages[lang].registrationFlow.pending;

  if (status !== "pending") {
    return null;
  }

  const titleId = "pending-approval-title";
  const emailTrimmed = email?.trim() ?? "";
  const emailDisplay = emailTrimmed || t.emailNotProvided;

  return (
    <div className="pending-approval-step w-100 d-flex flex-column align-items-center justify-content-center text-center">
      <div className="pending-approval-step__inner">
        <div className="pending-approval-step__icon-wrap mb-4" aria-hidden>
          <CupIcon className="pending-approval-step__cup-icon" focusable="false" />
        </div>

        <h2 className="step-title" id={titleId}>
          {renderPendingTitle(t, t.statusHighlight ?? status)}
        </h2>

        <p className="step-description" aria-describedby={titleId}>
          {renderPendingDescription(t)}
        </p>

        <div className="pending-approval-step__email-card w-100">
          <div className="pending-approval-step__email-icon-wrap" aria-hidden>
            <EmailOutlinedIcon className="pending-approval-step__email-icon-svg" />
          </div>
          <div className="pending-approval-step__email-text">
            <p
              className={`pending-approval-step__email-label mb-0 ${
                lang === "en"
                  ? "pending-approval-step__email-label"
                  : ""
              }`}
            >
              {t.emailLabel}
            </p>
            <p
              className="pending-approval-step__email-value mb-0 text-break"
            >
              {emailDisplay}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

PendingApproval.propTypes = {
  status: PropTypes.string,
  email: PropTypes.string,
};

PendingApproval.defaultProps = {
  status: "",
  email: "",
};

export default PendingApproval;
